import { useState } from 'react';

import { MailOutlined, UserOutlined } from '@ant-design/icons';
import { Form, Input, Button, Modal, App as AntApp, Upload } from 'antd';
import { useTranslation } from 'react-i18next';

import { useAppDispatch } from '@/hooks/useAppDispatch';

import { useUpdateProfileMutation } from '../api/authApi';
import { setUser } from '../stores/authSlice';
import type { UpdateProfilePayload } from '../types/auth.types';

interface ProfileEditModalProps {
  open: boolean;
  onClose: () => void;
  initialDisplayName?: string;
  initialEmail?: string;
  initialPhone?: string;
}

export function ProfileEditModal({
  open,
  onClose,
  initialDisplayName = '',
  initialEmail = '',
  initialPhone = '',
}: ProfileEditModalProps) {
  const { t } = useTranslation();
  const { message } = AntApp.useApp();
  const dispatch = useAppDispatch();
  const [form] = Form.useForm();
  const [avatarFile, setAvatarFile] = useState<File | null>(null);
  const [updateProfile, { isLoading }] = useUpdateProfileMutation();

  const handleOk = async () => {
    try {
      const values = await form.validateFields();
      const payload: UpdateProfilePayload = {
        display_name: values.display_name,
        email: values.email,
        phone: values.phone,
      };
      if (avatarFile) {
        payload.avatar = avatarFile;
      }
      const updated = await updateProfile(payload).unwrap();
      dispatch(setUser(updated));
      message.success(t('auth.profileUpdateSuccess'));
      form.resetFields();
      setAvatarFile(null);
      onClose();
    } catch {
      message.error(t('auth.profileUpdateFailed'));
    }
  };

  const handleCancel = () => {
    form.resetFields();
    setAvatarFile(null);
    onClose();
  };

  return (
    <Modal
      title={t('auth.profileEditTitle')}
      open={open}
      onCancel={handleCancel}
      footer={[
        <Button key="cancel" onClick={handleCancel}>
          {t('common.cancel', { defaultValue: 'Hủy' })}
        </Button>,
        <Button key="submit" type="primary" loading={isLoading} onClick={handleOk}>
          {t('common.save', { defaultValue: 'Save' })}
        </Button>,
      ]}
      destroyOnHidden
      centered
      width={480}
      afterOpenChange={(opened) => {
        if (opened) {
          form.setFieldsValue({
            display_name: initialDisplayName,
            email: initialEmail,
            phone: initialPhone,
          });
          setAvatarFile(null);
        }
      }}
    >
      <Form form={form} layout="vertical" autoComplete="off" className="pt-2">
        <Form.Item name="display_name" label={t('auth.displayName')}>
          <Input prefix={<UserOutlined />} placeholder={t('auth.displayName')} />
        </Form.Item>
        <Form.Item
          name="email"
          label={t('auth.email')}
          rules={[{ type: 'email', message: t('auth.invalidEmailFormat') }]}
        >
          <Input prefix={<MailOutlined />} placeholder={t('auth.email')} />
        </Form.Item>
        <Form.Item name="phone" label={t('auth.phone')}>
          <Input placeholder={t('auth.phone')} />
        </Form.Item>
        <Form.Item label={t('auth.avatar')}>
          <Upload
            maxCount={1}
            beforeUpload={(file) => {
              setAvatarFile(file);
              return false;
            }}
            onRemove={() => setAvatarFile(null)}
            accept="image/*"
          >
            <Button>{t('auth.selectAvatar')}</Button>
          </Upload>
        </Form.Item>
      </Form>
    </Modal>
  );
}
